import { FC } from 'react';
import { useComments } from '@/hooks/useComments';

interface Props {
  promptId: number;
  isLoggedIn: boolean;
  onAuthRequired: () => void;
  currentUserId: string;
}

const CommentsSection: FC<Props> = ({ promptId, isLoggedIn, onAuthRequired, currentUserId }) => {
  const { comments, commentsLoading, newCommentContent, statusMessage, setNewCommentContent, handleAddComment, handleCommentVote } = useComments({ promptId, isLoggedIn, currentUserId });

  return (
    <div className="mt-8 pt-2 border-t border-gray-200">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Comments</h3>

      {statusMessage && (<p className="text-center text-sm mb-4 text-brand-electric-blue" role="status" aria-live="polite">{statusMessage}</p>)}

      {isLoggedIn ? (
        <form onSubmit={handleAddComment} className="mb-6">
          <textarea
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-electric-blue resize-y"
            rows={3}
            placeholder="Write a comment..."
            value={newCommentContent}
            onChange={(e) => setNewCommentContent(e.target.value)}
            aria-label="Comment input"
          />
          <button
            type="submit"
            className="mt-2 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium py-2 px-4 rounded-md shadow-md transition-colors duration-200"
            aria-label="Post comment"
          >
            Post Comment
          </button>
        </form>
      ) : (
        // Prompt to log in to leave a comment 
        <div className="mb-6 p-4 bg-brand-light-gray border border-gray-200 text-brand-dark-blue rounded-md text-center">
          Please <button onClick={onAuthRequired} className="font-semibold underline">log in</button> or <button onClick={onAuthRequired} className="font-semibold underline">register</button> to leave a comment.
        </div>
      )}

      {commentsLoading ?
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-electric-blue mx-auto"></div>
        </div>
        :
        <div className="space-y-4">
          {comments.length > 0 ? (
            comments.map((comment) => {
              const authorName = `${comment.first_name || ''} ${comment.last_name ? comment.last_name.charAt(0) : ''}`.trim();
              return (
                <div key={comment.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-gray-800">{`${authorName}` || `User ${comment.user_id}`}</p>
                    <p className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-gray-700 text-sm mb-3">{comment.content}</p>
                  <div className="flex items-center space-x-3">
                    {/* Upvote button for comments  */}
                    <button
                      onClick={() => handleCommentVote(comment.id, 1)}
                      className={`flex items-center text-sm ${comment.myVote === 1 ? 'text-brand-electric-blue' : 'text-gray-500 hover:text-brand-electric-blue'}`}
                      aria-label={`Upvote comment by ${authorName}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                      Upvote
                    </button>
                    <span className="text-sm font-semibold text-gray-800">{comment.votes_count}</span>
                    {/* Downvote button for comments */}
                    <button
                      onClick={() => handleCommentVote(comment.id, -1)}
                      className={`flex items-center text-sm ${comment.myVote === -1 ? 'text-red-600' : 'text-gray-500 hover:text-red-600'}`}
                      aria-label={`Downvote comment by ${authorName}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                      Downvote
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 text-center">No comments yet. Be the first!</p>
          )}
        </div>
      }
    </div>
  );
};

export default CommentsSection;
